// Wissens-Check (A5): a short pre/post check of a topic the class is being
// taught. Shown before the story when the child's class has a check open.
// The child never sees whether an answer was right — the check measures what
// the child knows before and after the lessons, so it must not teach the
// answers. Finishing earns a small XP reward whatever the score.

import { useMemo, useState } from 'react';
import { answerCheck } from '../api/client';
import type { WissensCheck } from '../api/client';

interface Props {
  check: WissensCheck;
  childName: string;
  /** Called when the check is finished (xp = reward) or postponed (xp = 0). */
  onDone: (xp: number) => void;
}

type Step = 'intro' | 'question' | 'done';

const card: React.CSSProperties = {
  background: 'var(--parchment)', padding: 28, borderRadius: 8,
  width: 'min(560px, 94vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  fontFamily: 'var(--serif)', maxHeight: '90vh', overflowY: 'auto',
};

const primary: React.CSSProperties = {
  minHeight: 48, padding: '10px 24px', border: 'none', borderRadius: 6,
  background: 'var(--amber)', color: '#fff', fontFamily: 'var(--sans)',
  fontSize: 16, fontWeight: 600, cursor: 'pointer',
};

const quiet: React.CSSProperties = {
  minHeight: 44, padding: '8px 18px', background: 'transparent', border: 'none',
  fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--ink-muted)',
  cursor: 'pointer', textDecoration: 'underline',
};

export default function WissensCheckOverlay({ check, childName, onDone }: Props) {
  const open = useMemo(
    () => check.items.filter(i => !check.answered.includes(i.exercise_id)),
    [check],
  );
  const [step, setStep] = useState<Step>(open.length ? 'intro' : 'done');
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState('');
  const [sending, setSending] = useState(false);
  const [xp, setXp] = useState(0);
  const [error, setError] = useState('');

  const total = check.items.length;
  const item = open[idx];
  const number = total - open.length + idx + 1;

  async function send(answer: string) {
    if (sending || !item || !answer.trim()) return;
    setSending(true);
    setError('');
    try {
      const res = await answerCheck(check, item.exercise_id, answer);
      setTyped('');
      if (res.done || idx + 1 >= open.length) {
        setXp(res.xp_awarded);
        setStep('done');
      } else {
        setIdx(idx + 1);
      }
    } catch {
      setError('Das hat nicht geklappt. Tippe bitte noch einmal.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="check-title" style={{
      position: 'fixed', inset: 0, background: 'rgba(30,24,18,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9500,
    }}>
      <div style={card}>
        {step === 'intro' && (
          <>
            <h2 id="check-title" style={{ margin: 0, fontFamily: 'var(--display)', fontSize: 24, textAlign: 'center' }}>
              Wissens-Check: {check.title}
            </h2>
            <p style={{ margin: '14px 0 22px', fontSize: 15, lineHeight: 1.6, textAlign: 'center' }}>
              {childName ? `${childName}, ` : ''}Finn möchte wissen, was du über dieses Thema schon weißt.
              Es sind {open.length} kurze Fragen. Du erfährst hier nicht, ob deine Antwort stimmt —
              wenn du etwas nicht weißt, rate einfach. Danach geht die Geschichte weiter.
            </p>
            <div style={{ textAlign: 'center' }}>
              <button style={primary} onClick={() => setStep('question')}>Los geht&apos;s</button>
            </div>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button style={quiet} onClick={() => onDone(0)}>Später</button>
            </div>
          </>
        )}

        {step === 'question' && item && (
          <>
            <div id="check-title" style={{
              fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
              textTransform: 'uppercase', color: 'var(--ink-muted)', textAlign: 'center',
            }}>
              {check.title} · Frage {number} von {total}
            </div>
            <p style={{ fontSize: 19, lineHeight: 1.5, margin: '16px 0 20px', textAlign: 'center' }}>
              {item.question_text}
            </p>
            {item.options.length > 0 ? (
              <div style={{ display: 'grid', gap: 10 }}>
                {item.options.map(opt => (
                  <button key={opt} disabled={sending} onClick={() => send(opt)} style={{
                    minHeight: 52, padding: '10px 14px', borderRadius: 6, textAlign: 'left',
                    background: 'var(--card, #fff)', border: '1.5px solid rgba(60,50,40,0.2)',
                    fontFamily: 'var(--serif)', fontSize: 16, color: 'var(--ink)',
                    cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.6 : 1,
                  }}>{opt}</button>
                ))}
              </div>
            ) : (
              <form onSubmit={e => { e.preventDefault(); void send(typed); }}
                style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <input value={typed} onChange={e => setTyped(e.target.value)} aria-label="Deine Antwort"
                  inputMode={item.input_kind === 'keypad' ? 'numeric' : 'text'} maxLength={200}
                  style={{ fontSize: 18, padding: '10px 12px', borderRadius: 6, border: '1.5px solid rgba(60,50,40,0.3)' }} />
                <button type="submit" style={primary} disabled={sending || !typed.trim()}>Weiter</button>
              </form>
            )}
            {error && <p role="alert" style={{ color: 'var(--wrong)', fontSize: 14, textAlign: 'center' }}>{error}</p>}
          </>
        )}

        {step === 'done' && (
          <>
            <h2 id="check-title" style={{ margin: 0, fontFamily: 'var(--display)', fontSize: 24, textAlign: 'center' }}>
              Geschafft!
            </h2>
            <p style={{ margin: '14px 0 22px', fontSize: 15, lineHeight: 1.6, textAlign: 'center' }}>
              Danke! Jetzt weiß Finn, was du schon kannst.{xp > 0 ? ` Du bekommst ${xp} Erfahrungspunkte.` : ''}
            </p>
            <div style={{ textAlign: 'center' }}>
              <button style={primary} onClick={() => onDone(xp)}>Weiter zur Geschichte</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
